import { useState, useCallback, useEffect, useRef } from "react";

export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

import { buildFilterDto } from "./filterUtils";
import type { FilterDto } from "./filterUtils";

export interface DataTableApiConfig {
  fetchData: (dto: FilterDto) => Promise<{ data: any[]; total: number }>;
  filterConfig?: any;
  defaultSort?: { key: string; direction: "asc" | "desc" };
}

// useDataTableApi now supports debounce/minLength for text filters generically
export function useDataTableApi(tableConfig: DataTableApiConfig) {
  // Filters
  const [filters, setFilters] = useState<Record<string, any>>({});
  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Sorting
  const [sort, setSort] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>((tableConfig as any).defaultSort || null);
  // Data
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch data from backend
  const handleFetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Build backend filter/sort/pagination DTO

      const effectiveSort = sort ?? (tableConfig as any).defaultSort ?? null;
      const dto = buildFilterDto({
        filters,
        sortKey: effectiveSort?.key,
        sortDirection: effectiveSort?.direction,
        page,
        pageSize,
        ...(tableConfig.filterConfig
          ? { filterConfig: tableConfig.filterConfig }
          : {}),
      });
      // Call fetchData with the full FilterDto
      const result = await tableConfig.fetchData(dto);
      setData(result.data);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || "Failed to fetch data");
      console.error("[useDataTableApi] Error in handleFetchData:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, sort, page, pageSize, tableConfig.filterConfig]);

  // Refetch on dependency change
  useEffect(() => {
    handleFetchData();
  }, [handleFetchData]);

  return {
    data,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    filters,
    setFilters,
    loading,
    error,
  };
}
